import React, { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Check,
  Clock3,
  MessageCircle,
  Plus,
  Save,
  Settings2,
  Trash2,
  X,
} from "lucide-react";
import { getAccessToken } from "../../../../awsAuth";
import "./Booking.scss";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

const DAYS = [
  { key: "mon", label: "Пн" },
  { key: "tue", label: "Вт" },
  { key: "wed", label: "Ср" },
  { key: "thu", label: "Чт" },
  { key: "fri", label: "Пт" },
  { key: "sat", label: "Сб" },
  { key: "sun", label: "Нд" },
];

const DEFAULT_INTERVAL = { from: "09:00", to: "18:00" };

const DEFAULT_WORKING_DAYS = {
  mon: { enabled: true, intervals: [DEFAULT_INTERVAL] },
  tue: { enabled: true, intervals: [DEFAULT_INTERVAL] },
  wed: { enabled: true, intervals: [DEFAULT_INTERVAL] },
  thu: { enabled: true, intervals: [DEFAULT_INTERVAL] },
  fri: { enabled: true, intervals: [DEFAULT_INTERVAL] },
  sat: { enabled: false, intervals: [] },
  sun: { enabled: false, intervals: [] },
};

function safeObj(v) {
  return v && typeof v === "object" ? v : {};
}

function normalizeSettings(raw = {}) {
  const r = safeObj(raw);

  return {
    enabled: typeof r.enabled === "boolean" ? r.enabled : true,
    manualConfirm:
      typeof r.manualConfirm === "boolean" ? r.manualConfirm : true,
    slotDuration: Number(r.slotDuration || 60),
    bufferMinutes: Number(r.bufferMinutes || 15),
    maxDaysAhead: Number(r.maxDaysAhead || 30),
    timezone: r.timezone || "Europe/Kyiv",
    holidays: Array.isArray(r.holidays) ? r.holidays : [],
    vacation: Array.isArray(r.vacation) ? r.vacation : [],
    workingDays: {
      ...DEFAULT_WORKING_DAYS,
      ...(r.workingDays || {}),
    },
  };
}

function makeTimeOptions(step = 30) {
  const out = [];

  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += step) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }

  out.push("00:00");
  return Array.from(new Set(out));
}

const TIME_OPTIONS = makeTimeOptions(30);

function statusLabel(status) {
  const map = {
    pending: "Очікує підтвердження",
    confirmed: "Підтверджено",
    declined: "Відхилено",
    cancelled: "Скасовано",
    reschedule_proposed: "Запропоновано інший час",
    reschedule_requested: "Клієнт хоче перенести",
    completed: "Завершено",
  };

  return map[status] || status || "Запис";
}

function statusTone(status) {
  if (status === "confirmed") return "green";
  if (status === "declined" || status === "cancelled") return "red";
  if (status === "reschedule_proposed" || status === "reschedule_requested")
    return "blue";
  return "orange";
}

export default function Booking({ ownerId, readonly = false }) {
  const [settings, setSettings] = useState(() => normalizeSettings({}));
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [holidayDraft, setHolidayDraft] = useState("");
  const [vacationDraft, setVacationDraft] = useState({ from: "", to: "" });
  const [ownerComment, setOwnerComment] = useState({});
  const [proposalDraft, setProposalDraft] = useState({});

  const isReadonly = !!readonly;

  const sortedRequests = useMemo(() => {
    return [...requests].sort((a, b) => {
      const ad = `${a.date || ""} ${a.start || ""}`;
      const bd = `${b.date || ""} ${b.start || ""}`;
      return ad.localeCompare(bd);
    });
  }, [requests]);

  const pendingCount = requests.filter((x) =>
    ["pending", "reschedule_requested"].includes(x.status)
  ).length;

  async function load() {
    try {
      setLoading(true);

      const token = await getAccessToken().catch(() => null);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const settingsUrl = isReadonly
        ? `${API_BASE}/api/business/booking/settings/user/${encodeURIComponent(
            ownerId
          )}`
        : `${API_BASE}/api/business/booking/settings/me`;

      const settingsRes = await fetch(settingsUrl, { headers });
      const settingsData = await settingsRes.json().catch(() => ({}));

      if (settingsRes.ok) {
        setSettings(normalizeSettings(settingsData?.settings || {}));
      }

      if (!isReadonly) {
        const reqRes = await fetch(
          `${API_BASE}/api/business/booking/requests/me?role=owner`,
          {
          headers,
        });
        const reqData = await reqRes.json().catch(() => ({}));

        if (reqRes.ok) {
          setRequests(Array.isArray(reqData?.items) ? reqData.items : []);
        }
      }
    } catch (err) {
      console.error("[Booking] load error:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [ownerId, isReadonly]);

  const saveSettings = async (nextSettings = settings) => {
    if (isReadonly) return;

    setSaving(true);

    try {
      const token = await getAccessToken();

      const res = await fetch(
        `${API_BASE}/api/business/booking/settings/me`,
        {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(nextSettings),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося зберегти запис");
      }

      setSettings(normalizeSettings(data?.settings || nextSettings));
    } catch (err) {
      console.error("[Booking] save settings error:", err);
    } finally {
      setSaving(false);
    }
  };

  const patchSettings = (patch) => {
    const next = normalizeSettings({
      ...settings,
      ...patch,
    });

    setSettings(next);
  };

  const toggleDay = (dayKey) => {
    const day = safeObj(settings.workingDays?.[dayKey]);
    const nextWorkingDays = {
      ...settings.workingDays,
      [dayKey]: {
        enabled: !day.enabled,
        intervals: day.enabled ? day.intervals || [] : [DEFAULT_INTERVAL],
      },
    };

    patchSettings({ workingDays: nextWorkingDays });
  };

  const addInterval = (dayKey) => {
    const day = safeObj(settings.workingDays?.[dayKey]);

    const nextWorkingDays = {
      ...settings.workingDays,
      [dayKey]: {
        enabled: true,
        intervals: [...(day.intervals || []), DEFAULT_INTERVAL],
      },
    };

    patchSettings({ workingDays: nextWorkingDays });
  };

  const updateInterval = (dayKey, index, field, value) => {
    const day = safeObj(settings.workingDays?.[dayKey]);
    const intervals = Array.isArray(day.intervals) ? day.intervals : [];

    const nextIntervals = intervals.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );

    const nextWorkingDays = {
      ...settings.workingDays,
      [dayKey]: {
        enabled: true,
        intervals: nextIntervals,
      },
    };

    patchSettings({ workingDays: nextWorkingDays });
  };

  const removeInterval = (dayKey, index) => {
    const day = safeObj(settings.workingDays?.[dayKey]);
    const intervals = Array.isArray(day.intervals) ? day.intervals : [];

    const nextIntervals = intervals.filter((_, i) => i !== index);

    const nextWorkingDays = {
      ...settings.workingDays,
      [dayKey]: {
        enabled: nextIntervals.length > 0,
        intervals: nextIntervals,
      },
    };

    patchSettings({ workingDays: nextWorkingDays });
  };

  const addHoliday = () => {
    const value = String(holidayDraft || "").trim();
    if (!value) return;

    const next = Array.from(new Set([...(settings.holidays || []), value]));

    setHolidayDraft("");
    patchSettings({ holidays: next });
  };

  const removeHoliday = (date) => {
    patchSettings({
      holidays: (settings.holidays || []).filter((x) => x !== date),
    });
  };

  const addVacation = () => {
    if (!vacationDraft.from || !vacationDraft.to) return;

    patchSettings({
      vacation: [...(settings.vacation || []), vacationDraft],
    });

    setVacationDraft({ from: "", to: "" });
  };

  const removeVacation = (index) => {
    patchSettings({
      vacation: (settings.vacation || []).filter((_, i) => i !== index),
    });
  };

  const updateBooking = async (bookingId, payload) => {
    if (isReadonly) return;

    setSaving(true);

    try {
      const token = await getAccessToken();

      const res = await fetch(
        `${API_BASE}/api/business/booking/requests/${encodeURIComponent(
          bookingId
        )}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося оновити запис");
      }

      const updated = data?.booking;

      if (updated?.bookingId) {
        setRequests((prev) =>
          prev.map((item) =>
            item.bookingId === updated.bookingId ? updated : item
          )
        );
      }
    } catch (err) {
      console.error("[Booking] update booking error:", err);
    } finally {
      setSaving(false);
    }
  };

  const proposeSlot = (booking) => {
    const key = booking.bookingId;
    const draft = proposalDraft[key] || {};

    if (!draft.date || !draft.start || !draft.end) return;

    updateBooking(key, {
      action: "propose",
      comment: ownerComment[key] || "",
      proposedSlots: [
        {
          date: draft.date,
          start: draft.start,
          end: draft.end,
        },
      ],
    });
  };

  if (loading) {
    return (
      <div className="bpBooking">
        <div className="bpBooking__state">Завантаження онлайн-запису...</div>
      </div>
    );
  }

  if (isReadonly && !settings.enabled) return null;

  if (isReadonly) {
    return (
      <div className="bpBooking bpBooking--readonly">
        <div className="bpBooking__head">
          <div>
            <strong>Онлайн-запис</strong>
            <span>Оберіть послугу, дату та зручний час</span>
          </div>

          <div className="bpBooking__badge">
            <CalendarDays size={16} />
            Доступно
          </div>
        </div>

        <div className="bpBookingPublic">
          <div className="bpBookingPublic__icon">
            <CalendarDays size={28} strokeWidth={2.4} />
          </div>

          <div>
            <strong>Запис доступний</strong>
            <p>
              Бізнес приймає онлайн-запити. Після відправки власник підтвердить
              або запропонує інший час.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bpBooking">
      <div className="bpBooking__head">
        <div>
          <strong>Онлайн-запис</strong>
          <span>Графік, слоти, заявки та підтвердження клієнтів</span>
        </div>

        <button type="button" onClick={() => saveSettings()} disabled={saving}>
          <Save size={18} strokeWidth={2.7} />
          Зберегти
        </button>
      </div>

      <div className="bpBooking__switchCard">
        <div>
          <Settings2 size={22} strokeWidth={2.35} />
          <span>
            <strong>Онлайн-запис</strong>
            <em>
              {settings.enabled
                ? "Клієнти можуть надсилати запити"
                : "Модуль вимкнений"}
            </em>
          </span>
        </div>

        <button
          type="button"
          className={settings.enabled ? "is-on" : ""}
          onClick={() => patchSettings({ enabled: !settings.enabled })}
        >
          <span />
        </button>
      </div>

      <div className="bpBooking__grid">
        <label>
          Тривалість слота
          <select
            value={settings.slotDuration}
            onChange={(e) =>
              patchSettings({ slotDuration: Number(e.target.value) })
            }
          >
            <option value={15}>15 хв</option>
            <option value={30}>30 хв</option>
            <option value={45}>45 хв</option>
            <option value={60}>60 хв</option>
            <option value={90}>90 хв</option>
            <option value={120}>120 хв</option>
          </select>
        </label>

        <label>
          Буфер між записами
          <select
            value={settings.bufferMinutes}
            onChange={(e) =>
              patchSettings({ bufferMinutes: Number(e.target.value) })
            }
          >
            <option value={0}>Без буфера</option>
            <option value={5}>5 хв</option>
            <option value={10}>10 хв</option>
            <option value={15}>15 хв</option>
            <option value={30}>30 хв</option>
          </select>
        </label>

        <label>
          Днів наперед
          <select
            value={settings.maxDaysAhead}
            onChange={(e) =>
              patchSettings({ maxDaysAhead: Number(e.target.value) })
            }
          >
            <option value={7}>7 днів</option>
            <option value={14}>14 днів</option>
            <option value={30}>30 днів</option>
            <option value={60}>60 днів</option>
            <option value={90}>90 днів</option>
          </select>
        </label>

        <label className="bpBooking__check">
          <input
            type="checkbox"
            checked={settings.manualConfirm}
            onChange={(e) =>
              patchSettings({ manualConfirm: e.target.checked })
            }
          />
          Підтверджувати вручну
        </label>
      </div>

      <section className="bpBookingSection">
        <div className="bpBookingSection__head">
          <strong>Робочі години</strong>
          <span>Можна додати кілька інтервалів на день</span>
        </div>

        <div className="bpBookingDays">
          {DAYS.map((day) => {
            const value = safeObj(settings.workingDays?.[day.key]);
            const active = !!value.enabled;
            const intervals = Array.isArray(value.intervals)
              ? value.intervals
              : [];

            return (
              <div
                key={day.key}
                className={`bpBookingDay ${active ? "is-active" : ""}`}
              >
                <div className="bpBookingDay__top">
                  <button type="button" onClick={() => toggleDay(day.key)}>
                    <Check size={16} strokeWidth={2.8} />
                    {day.label}
                  </button>

                  <button type="button" onClick={() => addInterval(day.key)}>
                    <Plus size={16} strokeWidth={2.8} />
                    Інтервал
                  </button>
                </div>

                {active && intervals.length > 0 ? (
                  <div className="bpBookingIntervals">
                    {intervals.map((row, index) => (
                      <div key={`${day.key}-${index}`} className="bpBookingInterval">
                        <select
                          value={row.from}
                          onChange={(e) =>
                            updateInterval(day.key, index, "from", e.target.value)
                          }
                        >
                          {TIME_OPTIONS.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>

                        <span>—</span>

                        <select
                          value={row.to}
                          onChange={(e) =>
                            updateInterval(day.key, index, "to", e.target.value)
                          }
                        >
                          {TIME_OPTIONS.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          onClick={() => removeInterval(day.key, index)}
                        >
                          <Trash2 size={15} strokeWidth={2.6} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bpBookingDay__off">Вихідний</div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="bpBookingSection">
        <div className="bpBookingSection__head">
          <strong>Вихідні дати</strong>
          <span>Свята, неробочі дні та відпустка</span>
        </div>

        <div className="bpBookingDates">
          <div className="bpBookingDateAdd">
            <input
              type="date"
              value={holidayDraft}
              onChange={(e) => setHolidayDraft(e.target.value)}
            />

            <button type="button" onClick={addHoliday}>
              <Plus size={17} />
              Додати
            </button>
          </div>

          {!!settings.holidays.length && (
            <div className="bpBookingChips">
              {settings.holidays.map((date) => (
                <button
                  key={date}
                  type="button"
                  onClick={() => removeHoliday(date)}
                >
                  {date}
                  <X size={14} />
                </button>
              ))}
            </div>
          )}

          <div className="bpBookingVacation">
            <input
              type="date"
              value={vacationDraft.from}
              onChange={(e) =>
                setVacationDraft((prev) => ({
                  ...prev,
                  from: e.target.value,
                }))
              }
            />

            <span>—</span>

            <input
              type="date"
              value={vacationDraft.to}
              onChange={(e) =>
                setVacationDraft((prev) => ({
                  ...prev,
                  to: e.target.value,
                }))
              }
            />

            <button type="button" onClick={addVacation}>
              Відпустка
            </button>
          </div>

          {!!settings.vacation.length && (
            <div className="bpBookingChips">
              {settings.vacation.map((range, index) => (
                <button
                  key={`${range.from}-${range.to}-${index}`}
                  type="button"
                  onClick={() => removeVacation(index)}
                >
                  {range.from} — {range.to}
                  <X size={14} />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bpBookingSection">
        <div className="bpBookingSection__head">
          <strong>Заявки</strong>
          <span>
            {pendingCount > 0
              ? `${pendingCount} потребує дії`
              : "Нових заявок немає"}
          </span>
        </div>

        {!sortedRequests.length ? (
          <div className="bpBooking__empty">
            <Clock3 size={24} />
            <strong>Записів поки немає</strong>
            <span>Коли клієнт надішле запит, він зʼявиться тут.</span>
          </div>
        ) : (
          <div className="bpBookingRequests">
            {sortedRequests.map((item) => {
              const key = item.bookingId;
              const tone = statusTone(item.status);
              const comment = ownerComment[key] || "";
              const proposal = proposalDraft[key] || {};

              return (
                <article key={key} className="bpBookingRequest">
                  <div className="bpBookingRequest__top">
                    <div>
                      <strong>{item.clientName || "Клієнт"}</strong>
                      <span>
                        {item.serviceTitle || "Запис"} · {item.date} о{" "}
                        {item.start}
                      </span>
                    </div>

                    <em className={`is-${tone}`}>{statusLabel(item.status)}</em>
                  </div>

                  {item.comment && (
                    <p className="bpBookingRequest__comment">
                      <MessageCircle size={15} />
                      {item.comment}
                    </p>
                  )}

                  {["pending", "reschedule_requested"].includes(item.status) && (
                    <>
                      <textarea
                        value={comment}
                        onChange={(e) =>
                          setOwnerComment((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        placeholder="Коментар клієнту, якщо потрібно"
                      />

                      <div className="bpBookingRequest__actions">
                        <button
                          type="button"
                          className="is-green"
                          onClick={() =>
                            updateBooking(key, {
                              action: "confirm",
                              comment,
                            })
                          }
                          disabled={saving}
                        >
                          Підтвердити
                        </button>

                        <button
                          type="button"
                          className="is-red"
                          onClick={() =>
                            updateBooking(key, {
                              action: "decline",
                              comment,
                            })
                          }
                          disabled={saving}
                        >
                          Відхилити
                        </button>
                      </div>

                      <div className="bpBookingProposal">
                        <input
                          type="date"
                          value={proposal.date || ""}
                          onChange={(e) =>
                            setProposalDraft((prev) => ({
                              ...prev,
                              [key]: {
                                ...safeObj(prev[key]),
                                date: e.target.value,
                              },
                            }))
                          }
                        />

                        <select
                          value={proposal.start || ""}
                          onChange={(e) =>
                            setProposalDraft((prev) => ({
                              ...prev,
                              [key]: {
                                ...safeObj(prev[key]),
                                start: e.target.value,
                              },
                            }))
                          }
                        >
                          <option value="">Початок</option>
                          {TIME_OPTIONS.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>

                        <select
                          value={proposal.end || ""}
                          onChange={(e) =>
                            setProposalDraft((prev) => ({
                              ...prev,
                              [key]: {
                                ...safeObj(prev[key]),
                                end: e.target.value,
                              },
                            }))
                          }
                        >
                          <option value="">Кінець</option>
                          {TIME_OPTIONS.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>

                        <button
                          type="button"
                          onClick={() => proposeSlot(item)}
                          disabled={saving}
                        >
                          Запропонувати
                        </button>
                      </div>
                    </>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}